
import React from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";

import SEOHead from "@/components/common/SEOHead";
import { Camera, Users, Award, Target, MapPin, Phone, Mail, Eye, Zap, TrendingUp, Handshake, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import TocFotosCounter from "@/components/TocFotosCounter";

const About = () => {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["WebPage", "AboutPage"],
        "@id": "https://site.toctocbr.com/sobre#webpage",
        "url": "https://site.toctocbr.com/sobre",
        "name": "Sobre a Toc Toc",
        "inLanguage": "pt-BR",
        "about": [
          "fotógrafo imobiliário",
          "fotografia imobiliária profissional",
          "fotografia de arquitetura",
          "fotografia de interiores",
          "filmmaker imobiliário",
          "fotografia para Airbnb e Temporada"
        ],
        "isPartOf": { "@id": "https://site.toctocbr.com/#website" }
      },
      {
        "@type": "WebSite",
        "@id": "https://site.toctocbr.com/#website",
        "url": "https://site.toctocbr.com/",
        "name": "Toc Toc",
        "inLanguage": "pt-BR",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://site.toctocbr.com/?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
        "@id": "https://site.toctocbr.com/#org",
        "name": "Toc Toc",
        "url": "https://site.toctocbr.com/",
        "logo": "https://site.toctocbr.com/logo.png",
        "description": "Somos especialistas em fotografia imobiliária profissional, fotografia de arquitetura e interiores e filmmaker (vídeos imobiliários) para corretores, imobiliárias, construtoras e anfitriões de temporada em Santa Catarina.",
        "telephone": "+55-47-99736-7396",
        "email": "contato@toctocbr.com",
        "sameAs": ["https://www.instagram.com/toctoc.br"],
        "areaServed": [
          { "@type": "City", "name": "Balneário Camboriú" },
          { "@type": "City", "name": "Camboriú" },
          { "@type": "City", "name": "Itajaí" },
          { "@type": "City", "name": "Itapema" },
          { "@type": "City", "name": "Porto Belo" },
          { "@type": "City", "name": "Bombinhas" },
          { "@type": "City", "name": "Navegantes" },
          { "@type": "State", "name": "Santa Catarina" },
          { "@type": "Country", "name": "Brasil" }
        ]
      },
      {
        "@type": "ItemList",
        "@id": "https://site.toctocbr.com/sobre#servicos",
        "name": "O que fazemos",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "item": { "@id": "https://site.toctocbr.com/#serv-fotografo-imobiliario" } },
          { "@type": "ListItem", "position": 2, "item": { "@id": "https://site.toctocbr.com/#serv-arquitetura-interiores" } },
          { "@type": "ListItem", "position": 3, "item": { "@id": "https://site.toctocbr.com/#serv-filmmaker" } },
          { "@type": "ListItem", "position": 4, "item": { "@id": "https://site.toctocbr.com/#serv-airbnb" } }
        ]
      },
      {
        "@type": "Service",
        "@id": "https://site.toctocbr.com/#serv-fotografo-imobiliario",
        "serviceType": "Fotógrafo Imobiliário (Fotografia Imobiliária Profissional)",
        "provider": { "@id": "https://site.toctocbr.com/#org" },
        "areaServed": [
          "Balneário Camboriú",
          "Camboriú",
          "Itajaí",
          "Itapema",
          "Porto Belo",
          "Bombinhas",
          "Navegantes",
          "Santa Catarina",
          "Brasil"
        ],
        "description": "Ensaios profissionais de imóveis para venda e locação, com luz, composição e edição premium. Entrega em até 48h."
      },
      {
        "@type": "Service",
        "@id": "https://site.toctocbr.com/#serv-arquitetura-interiores",
        "serviceType": "Fotografia de Arquitetura e Interiores",
        "provider": { "@id": "https://site.toctocbr.com/#org" },
        "description": "Fotografia técnica de projetos arquitetônicos e ambientes internos, com fidelidade cromática e narrativa visual."
      },
      {
        "@type": "Service",
        "@id": "https://site.toctocbr.com/#serv-filmmaker",
        "serviceType": "Filmmaker Imobiliário (Vídeos Imobiliários)",
        "provider": { "@id": "https://site.toctocbr.com/#org" },
        "description": "Produção de vídeos imobiliários e versões para redes sociais (Reels/Shorts), com captação e edição profissional."
      },
      {
        "@type": "Service",
        "@id": "https://site.toctocbr.com/#serv-airbnb",
        "serviceType": "Fotografia para Airbnb e Temporada",
        "provider": { "@id": "https://site.toctocbr.com/#org" },
        "description": "Ensaios para anfitriões e administradores de temporada, destacando experiências e ambientes que convertem mais reservas."
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://site.toctocbr.com/sobre#breadcrumbs",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://site.toctocbr.com/" },
          { "@type": "ListItem", "position": 2, "name": "Sobre", "item": "https://site.toctocbr.com/sobre" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Sobre a Toc Toc | Fotógrafo Imobiliário, Arquitetura e Vídeos"
        description="Equipe especializada em fotografia imobiliária, arquitetura/interiores, filmmaker e social media. Integramos conteúdo a ferramentas de gestão (ex.: Hunter CRM)."
        keywords="sobre Toc Toc, fotógrafo imobiliário, fotografia imobiliária, fotografia de arquitetura, fotografia de interiores, vídeos imobiliários, filmmaker, fotografia para Airbnb, Santa Catarina, Balneário Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas, Navegantes"
        canonicalUrl="https://site.toctocbr.com/sobre"
        robots="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        ogImage="https://site.toctocbr.com/og-image.jpg"
        ogTitle="Sobre a Toc Toc | Fotografia Imobiliária & Arquitetura"
        ogDescription="Especialistas em fotografia de imóveis, arquitetura e interiores, filmmaker e fotos para Airbnb. Santa Catarina e cidades da região."
        twitterTitle="Sobre a Toc Toc | Fotógrafo Imobiliário em SC"
        twitterDescription="Fotografia imobiliária, arquitetura/interiores, filmmaker e Airbnb. BC, Camboriú, Itajaí, Itapema, Porto Belo, Bombinhas e Navegantes."
        ogImageAlt="Toc Toc — fotografia imobiliária profissional"
        titleTemplate={null}
        schema={schema}
        prefetchLinks={["/servicos", "/portfolio", "/cidades-atendidas", "/contato"]}
      />
      
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Quem <span className="text-[#FF5605]">Somos</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  A plataforma que integra o mais completo ecossistema de vendas, 
                  dados e marketing do mercado imobiliário em Santa Catarina.
                </p>
              </div>
            </div>
          </section>

          {/* Nossa História */}
          <section className="py-16 md:py-24 bg-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-6">
                    Nossa <span className="text-[#FF5605]">Historia</span>
                  </h2>
                  <div className="space-y-6 text-lg text-[#5B5A59] leading-relaxed">
                    <p>
                      A <strong className="text-[#FF5605]">Toc Toc</strong> é a plataforma que integra o mais completo ecossistema de vendas, dados e marketing do mercado imobiliário. <strong className="text-[#FF5605]">Nascemos em 2019</strong> com um propósito claro: elevar o padrão da experiência imobiliária no Brasil, conectando tecnologia, performance visual e inteligência de mercado.
                    </p>
                    <p>
                      Unimos profissionais, imobiliárias e construtoras em uma jornada digital onde 
                      <strong className="text-[#FF5605]"> a imagem vende, os dados guiam, e o marketing converte</strong>. 
                      Com uma operação ágil, moderna e escalável, somos a ponte entre imóveis comuns e vendas extraordinárias.
                    </p>
                    <p>
                      Atendemos Balneário Camboriú, Itajaí, Itapema, Porto Belo, Camboriú e região com 
                      <strong className="text-[#FF5605]"> mais de 30 mil imóveis fotografados</strong>, 
                      desenvolvendo expertise única em marketing visual imobiliário.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <img 
                    src="/lovable-uploads/de23c8d2-05b8-4009-a2bf-1c4637e23daf.png"
                    alt="Fotografia imobiliária Balneário Camboriú"
                    className="rounded-2xl shadow-lg"
                    loading="eager"
                    width="300"
                    height="400"
                    decoding="async"
                  />
                  <img 
                    src="/lovable-uploads/5b71ac7b-5549-4fff-baac-b74e1f8e5bf8.png"
                    alt="Arquitetura profissional Itajaí"
                    className="rounded-2xl shadow-lg mt-8"
                    loading="eager"
                    width="300"
                    height="400"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Números */}
          <section className="py-16 bg-[#F6E5C9]/20">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                  Nossos <span className="text-[#FF5605]">Números</span>
                </h2>
                <p className="text-lg text-[#5B5A59]">
                  Resultados que comprovam nossa expertise no mercado imobiliário
                </p>
              </div>
              
              <div className="grid md:grid-cols-4 gap-8">
                <TocFotosCounter 
                  end={30}
                  suffix="K+"
                  title="Imóveis Fotografados"
                />
                
                <TocFotosCounter 
                  end={500}
                  suffix="+"
                  title="Clientes Atendidos"
                />
                
                <TocFotosCounter 
                  end={5}
                  suffix=""
                  title="Cidades Atendidas"
                />
                
                <TocFotosCounter 
                  end={5}
                  suffix=""
                  title="Anos de Experiência"
                />
              </div>
            </div>
          </section>

          {/* Missão, Visão, Valores */}
          <section className="py-16 md:py-24 bg-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Target className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">Missão</h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    Transformar o mercado imobiliário por meio de experiências visuais de alto impacto, 
                    inteligência de dados e estratégias de marketing sob medida, ajudando nossos clientes 
                    a vender mais, com mais eficiência e autoridade.
                  </p>
                </div>
                
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Eye className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">Visão</h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    Ser a plataforma número 1 do Brasil em marketing e vendas imobiliárias, 
                    reconhecida por sua capacidade de acelerar negócios, escalar resultados e 
                    profissionalizar o mercado com tecnologia, branding e performance.
                  </p>
                </div>
                
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Award className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">Valores</h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    Excelência visual, inovação contínua, foco em resultado, 
                    parceria com o cliente e agilidade com qualidade premium.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Nossos Valores Detalhados */}
          <section className="py-16 bg-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Nossos <span className="text-[#FF5605]">Valores</span>
                </h2>
                <p className="text-xl text-white/90">
                  Os pilares que guiam nossa jornada no mercado imobiliário
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <Eye className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Excelência Visual</h4>
                  <p className="text-white/80">
                    Acreditamos que imagem não é detalhe – é diferencial competitivo 
                    que transforma imóveis comuns em oportunidades extraordinárias.
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <Zap className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Inovação Contínua</h4>
                  <p className="text-white/80">
                    Evoluímos todos os dias para antecipar o futuro do mercado imobiliário 
                    com tecnologia e soluções disruptivas.
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Foco em Resultado</h4>
                  <p className="text-white/80">
                    Cada projeto precisa gerar impacto direto em vendas, cliques ou autoridade 
                    no mercado imobiliário.
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <Handshake className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Parceria com o Cliente</h4>
                  <p className="text-white/80">
                    Crescemos juntos com corretores, imobiliárias e incorporadoras, 
                    construindo relacionamentos duradouros.
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <Clock className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Agilidade com Qualidade</h4>
                  <p className="text-white/80">
                    Entregamos rápido, sem abrir mão do padrão premium que 
                    nossos clientes esperam e merecem.
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4">
                    <Award className="h-6 w-6 text-[#FF5605]" />
                  </div>
                  <h4 className="text-xl font-bold mb-3 text-[#FF5605]">Ecossistema Completo</h4>
                  <p className="text-white/80">
                    Oferecemos a plataforma mais completa do mercado: fotografia, 
                    vídeos, CRM, dados e marketing integrados.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Contato */}
          <section className="py-16 bg-white">
            <div className="container max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-6">
                Pronto para <span className="text-[#FF5605]">começar</span>?
              </h2>
              <p className="text-xl text-[#5B5A59] mb-8 max-w-2xl mx-auto">
                Entre em contato conosco e descubra como podemos transformar 
                seus imóveis comuns em vendas extraordinárias.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="flex items-center justify-center space-x-3">
                  <Phone className="h-6 w-6 text-[#FF5605]" />
                  <span className="text-lg">(47) 99736-7396</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Mail className="h-6 w-6 text-[#FF5605]" />
                  <span className="text-lg">contato@toctocbr.com</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <MapPin className="h-6 w-6 text-[#FF5605]" />
                  <span className="text-lg">Balneário Camboriú, SC</span>
                </div>
              </div>
              
              <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-12 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
                Solicitar Orçamento Grátis
              </Button>
            </div>
          </section>
        </main>
        <TocTocFooter />
      </div>
    </>
  );
};

export default About;
